import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { toast } from 'sonner';
import {
  History, Calendar, Clock, XCircle, Lightbulb, Info,
  ChevronRight, Trash2, Loader2, Search, Filter
} from 'lucide-react';

export default function SessionsPage() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const navigate = useNavigate();

  useEffect(() => {
    loadSessions();
  }, []);

  const loadSessions = async () => {
    setLoading(true);
    try {
      const data = await api.get('/api/sessions');
      setSessions(data);
    } catch (err) {
      toast.error('Error al cargar sesiones');
    }
    setLoading(false);
  };

  const deleteSession = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('¿Eliminar esta sesión?')) return;
    try {
      await api.del(`/api/sessions/${id}`);
      setSessions(prev => prev.filter(s => s.id !== id));
      toast.success('Sesión eliminada');
    } catch (err) {
      toast.error('Error al eliminar');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('es-ES', {
      day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const getDuration = (start, end) => {
    if (!start || !end) return '—';
    const ms = new Date(end) - new Date(start);
    const mins = Math.floor(ms / 60000);
    const secs = Math.floor((ms % 60000) / 1000);
    return `${mins}m ${secs}s`;
  };

  const cleanupStale = async () => {
    if (!window.confirm('¿Eliminar sesiones vacías sin transcripción?')) return;
    try {
      const result = await api.del('/api/sessions/cleanup/stale');
      toast.success(`${result.deleted} sesiones vacías eliminadas`);
      loadSessions();
    } catch (err) {
      toast.error('Error al limpiar sesiones');
    }
  };

  const filtered = sessions.filter(s => {
    const matchSearch = !searchQuery ||
      (s.agent_name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      s.id.toLowerCase().includes(searchQuery.toLowerCase());
    const matchStatus = statusFilter === 'all' || s.status === statusFilter;
    return matchSearch && matchStatus;
  });

  return (
    <div className="p-6 lg:p-8 max-w-6xl" data-testid="sessions-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
            Historial de Sesiones
          </h1>
          <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>
            Revisa tus llamadas anteriores y sugerencias generadas
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={cleanupStale}
            data-testid="cleanup-stale-btn"
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-all"
            style={{ backgroundColor: 'var(--surface-secondary)', color: 'var(--text-muted)' }}
          >
            <Trash2 size={12} />
            Limpiar vacías
          </button>
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg" style={{ backgroundColor: 'var(--surface-secondary)' }}>
            <History size={14} style={{ color: 'var(--text-muted)' }} />
            <span className="text-xs font-mono" style={{ color: 'var(--text-muted)' }}>
              {filtered.length} sesiones
            </span>
          </div>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-muted)' }} />
          <input
            type="text"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            data-testid="search-sessions"
            placeholder="Buscar por agente o ID..."
            className="w-full pl-10 pr-4 py-2.5 rounded-lg border text-sm outline-none"
            style={{ backgroundColor: 'var(--surface-primary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
          />
        </div>
        <div className="flex gap-1 p-1 rounded-lg" style={{ backgroundColor: 'var(--surface-secondary)' }}>
          {['all', 'active', 'completed'].map(status => (
            <button
              key={status}
              onClick={() => setStatusFilter(status)}
              data-testid={`filter-${status}`}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                statusFilter === status ? 'text-white shadow-sm' : ''
              }`}
              style={{
                backgroundColor: statusFilter === status ? 'var(--accent-primary)' : 'transparent',
                color: statusFilter === status ? '#fff' : 'var(--text-muted)',
              }}
            >
              {status === 'all' ? 'Todas' : status === 'active' ? 'Activas' : 'Completadas'}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 size={24} className="animate-spin" style={{ color: 'var(--accent-primary)' }} />
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center" style={{ color: 'var(--text-muted)' }}>
          <History size={48} className="mb-4 opacity-30" />
          <p className="text-lg font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>Sin sesiones</p>
          <p className="text-sm">Inicia una llamada para ver el historial aquí</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(session => (
            <div
              key={session.id}
              onClick={() => navigate(`/sessions/${session.id}`)}
              data-testid={`session-item-${session.id}`}
              className="flex items-center justify-between p-5 rounded-xl border cursor-pointer transition-all duration-200 hover:translate-x-1 hover:shadow-lg group"
              style={{
                backgroundColor: 'var(--surface-primary)',
                borderColor: 'var(--border-color)',
              }}
            >
              <div className="flex items-center gap-4">
                <div
                  className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    session.status === 'active' ? 'bg-green-500/20' : ''
                  }`}
                  style={session.status !== 'active' ? { backgroundColor: 'var(--surface-secondary)' } : {}}
                >
                  <History
                    size={18}
                    style={{ color: session.status === 'active' ? '#22c55e' : 'var(--text-muted)' }}
                  />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-heading font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>
                      {session.agent_name || 'Agente'}
                    </span>
                    <span
                      className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase ${
                        session.status === 'active' ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'
                      }`}
                    >
                      {session.status === 'active' ? 'Activa' : 'Completada'}
                    </span>
                    {session.source === 'twilio_live' && (
                      <span className="text-[10px] px-2 py-0.5 rounded-full font-bold uppercase bg-blue-500/20 text-blue-400" data-testid={`twilio-badge-${session.id}`}>
                        Twilio
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 mt-1">
                    <span className="flex items-center gap-1 text-xs" style={{ color: 'var(--text-muted)' }}>
                      <Calendar size={11} />
                      {formatDate(session.created_at)}
                    </span>
                    <span className="flex items-center gap-1 text-xs" style={{ color: 'var(--text-muted)' }}>
                      <Clock size={11} />
                      {getDuration(session.created_at, session.ended_at)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="flex items-center gap-3">
                  <SuggestionBadge icon={XCircle} count={session.error_count || 0} color="#ef4444" />
                  <SuggestionBadge icon={Lightbulb} count={session.opportunity_count || 0} color="#f59e0b" />
                  <SuggestionBadge icon={Info} count={session.tip_count || 0} color="#3b82f6" />
                </div>
                <button
                  onClick={(e) => deleteSession(session.id, e)}
                  data-testid={`delete-session-${session.id}`}
                  className="opacity-0 group-hover:opacity-100 p-2 rounded-lg transition-all hover:bg-red-500/20"
                >
                  <Trash2 size={14} className="text-red-400" />
                </button>
                <ChevronRight size={16} style={{ color: 'var(--text-muted)' }} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SuggestionBadge({ icon: Icon, count, color }) {
  return (
    <div className="flex items-center gap-1 text-xs font-mono" style={{ color }}>
      <Icon size={12} />
      <span>{count}</span>
    </div>
  );
}
